import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Link,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { AutoAwesome as AutoAwesomeIcon, TipsAndUpdates as TipsAndUpdatesIcon } from '@mui/icons-material';
import { PlansNotice, SUBMIT_SHORTCUT_LABEL, isSubmitShortcut } from './shared';

interface ChangeWithAIBarProps {
  value: string;
  onChange: (value: string) => void;
  onApply: () => void;
  busy: boolean;
  error: string | null;
  hasActiveSubscription: boolean;
  /** Name of the saved function; enables the "new version" option. Omit for an unsaved draft. */
  functionName?: string | null;
  saveAsNewVersion: boolean;
  onSaveAsNewVersionChange: (value: boolean) => void;
  /** Self-improvement page for this function, when it is available. */
  learningHref?: string;
  inputRef?: React.Ref<HTMLTextAreaElement>;
}

/** One-row "describe a change" bar above the form; applies the change to the form for review. */
export const ChangeWithAIBar: React.FC<ChangeWithAIBarProps> = ({
  value,
  onChange,
  onApply,
  busy,
  error,
  hasActiveSubscription,
  functionName,
  saveAsNewVersion,
  onSaveAsNewVersionChange,
  learningHref,
  inputRef,
}) => {
  if (!hasActiveSubscription) {
    return (
      <Box id="change-with-ai" sx={{ mb: 3 }}>
        <PlansNotice>Changing a function with AI is part of a paid plan. You can still edit the form below.</PlansNotice>
      </Box>
    );
  }

  const canApply = !!value.trim() && !busy;

  return (
    <Paper id="change-with-ai" variant="outlined" sx={{ mb: 3, p: 2, borderRadius: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
        <AutoAwesomeIcon color="primary" sx={{ mt: 1 }} aria-hidden />
        <TextField
          inputRef={inputRef}
          fullWidth
          size="small"
          multiline
          maxRows={6}
          label="Change it with AI"
          placeholder="Describe what to change, e.g. also return the brand name"
          value={value}
          disabled={busy}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => {
            if (isSubmitShortcut(e) && canApply) {
              e.preventDefault();
              onApply();
            }
          }}
        />
        <Button
          variant="contained"
          onClick={onApply}
          disabled={!canApply}
          startIcon={busy ? <CircularProgress size={16} color="inherit" /> : undefined}
          sx={{ height: 40, flexShrink: 0 }}
        >
          {busy ? 'Applying…' : 'Apply'}
        </Button>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', columnGap: 2, rowGap: 0.5, flexWrap: 'wrap', mt: 1, pl: 4.5 }}>
        <Typography variant="caption" color="text.secondary" aria-live="polite">
          {busy
            ? 'Working on it. This can take up to a minute.'
            : `Updates the form below for you to review. Nothing is saved until you click Save. (${SUBMIT_SHORTCUT_LABEL})`}
        </Typography>
        {functionName && (
          <FormControlLabel
            sx={{ mr: 0 }}
            control={
              <Checkbox size="small" checked={saveAsNewVersion} onChange={(e) => onSaveAsNewVersionChange(e.target.checked)} />
            }
            label={
              <Typography variant="caption">
                Save as a new version of <code>{functionName}</code>
                {!saveAsNewVersion && ' (off: saves a separate copy, the original stays as it is)'}
              </Typography>
            }
          />
        )}
        {learningHref && (
          <Link
            component={RouterLink}
            to={learningHref}
            variant="caption"
            underline="hover"
            sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
          >
            <TipsAndUpdatesIcon sx={{ fontSize: 14 }} />
            Improve it from its real runs with Self-improvement
          </Link>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mt: 1.5 }}>
          {error}
        </Alert>
      )}
    </Paper>
  );
};

export default ChangeWithAIBar;
